import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';

function AdminRestaurant() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const [plats, setPlats] = useState([]);
  const [editingPlat, setEditingPlat] = useState(null);
  const [form, setForm] = useState({ 
    name: '', 
    price: '', 
    description: '', 
    category: '', 
    image: null 
  });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [userSpecialites, setUserSpecialites] = useState([]);

  const specialitesCategories = ['Traditional', 'Seafood', 'Salads', 'Meat', 'Pastry'];
  
  const platsCategories = ['Plats principaux', 'Entrées', 'Desserts'];

  useEffect(() => {
    checkUser();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (user) {
      fetchPlats();
      checkUserSpecialites();
    }
    // eslint-disable-next-line
  }, [user]);

  const checkUser = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) {
        console.error('Erreur de récupération utilisateur:', userError);
        setError('Erreur d\'authentification: ' + userError.message);
        return;
      }
      setUser(user);
      console.log('Utilisateur connecté:', user);
    } catch (err) {
      console.error('Erreur inattendue lors de checkUser:', err);
      setError('Erreur inattendue: ' + err.message);
    }
  };

  const checkUserSpecialites = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('plat')
        .select('category')
        .eq('user_id', user.id)
        .in('category', specialitesCategories);
      
      if (error) {
        console.error('Erreur checkUserSpecialites:', error);
        return;
      }
      
      const userSpecialitesList = data.map(plat => plat.category);
      setUserSpecialites(userSpecialitesList);
      console.log('Spécialités déjà ajoutées par l\'utilisateur:', userSpecialitesList);
    } catch (err) {
      console.error('Erreur inattendue dans checkUserSpecialites:', err);
    }
  };

  const fetchPlats = async () => {
    try {
      console.log('Début de fetchPlats');
      const { data, error } = await supabase
        .from('plat')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Erreur fetchPlats:', error);
        setError('Erreur lors de la récupération des plats: ' + error.message);
        return;
      }
      
      console.log('Plats récupérés:', data);
      setPlats(data);
    } catch (err) {
      console.error('Erreur inattendue dans fetchPlats:', err);
      setError('Erreur inattendue: ' + err.message);
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPlats = plats.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(plats.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setForm(prev => ({ 
      ...prev, 
      [name]: files ? files[0] : value 
    }));
  };

  const uploadImage = async (file) => {
    if (!file) return null;
    
    try {
      console.log('Téléchargement de l\'image:', file.name);
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;
      
      const { error: uploadError } = await supabase
        .storage
        .from('plat-images')
        .upload(filePath, file);
      
      if (uploadError) {
        console.error('Erreur upload image:', uploadError);
        throw uploadError;
      }
      
      const { data: { publicUrl } } = supabase
        .storage
        .from('plat-images')
        .getPublicUrl(filePath);
      
      console.log('Image téléchargée, URL:', publicUrl);
      return publicUrl;
    } catch (error) {
      console.error('Erreur lors du téléchargement de l\'image:', error);
      throw error;
    }
  };

  const resetForm = () => {
    console.log('Réinitialisation du formulaire');
    setEditingPlat(null);
    setForm({ name: '', price: '', description: '', category: '', image: null });
  };

  const canAddSpecialite = (category) => {
    if (!specialitesCategories.includes(category)) return true;
    // Permettre la modification même si la catégorie est déjà utilisée
    if (editingPlat && editingPlat.category === category) return true;
    return !userSpecialites.includes(category);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('Vous devez être connecté pour effectuer cette action');
      }

      // Vérification pour les spécialités (uniquement pour les nouveaux plats)
      if (!editingPlat && specialitesCategories.includes(form.category)) {
        if (userSpecialites.includes(form.category)) {
          throw new Error(`Vous ne pouvez ajouter qu'une seule fois un plat dans la catégorie "${form.category}"`);
        }
      }

      console.log('User ID:', user.id);
      console.log('Soumission du formulaire, mode:', editingPlat ? 'édition' : 'création');

      let imageUrl = form.image ? null : (editingPlat ? editingPlat.image_url : '');
      
      if (form.image) {
        imageUrl = await uploadImage(form.image);
        console.log('Nouvelle URL image:', imageUrl);
      }

      if (editingPlat) {
        console.log('Mise à jour du plat ID:', editingPlat.id);
        
        const updateData = {
          name: form.name,
          price: parseFloat(form.price),
          description: form.description,
          category: form.category,
          user_id: user.id
        };
        
        if (imageUrl !== null) {
          updateData.image_url = imageUrl;
        }

        console.log('Données de mise à jour:', updateData);

        const { data, error } = await supabase
          .from('plat')
          .update(updateData)
          .eq('id', editingPlat.id)
          .select();

        if (error) {
          console.error('Erreur détaillée Supabase:', error);
          if (error.message && (error.message.includes('permission') || error.message.includes('row-level security'))) {
            throw new Error('Erreur de permission. Vérifiez les politiques RLS dans Supabase.');
          }
          throw error;
        }
        
        console.log('Réponse mise à jour:', data);
        setSuccess('Plat modifié avec succès');

        // Mettre à jour la liste des spécialités si la catégorie a changé
        if (editingPlat.category !== form.category) {
          // Retirer l'ancienne catégorie si c'était une spécialité
          if (specialitesCategories.includes(editingPlat.category)) {
            setUserSpecialites(prev => prev.filter(cat => cat !== editingPlat.category));
          }
          // Ajouter la nouvelle catégorie si c'est une spécialité
          if (specialitesCategories.includes(form.category)) {
            setUserSpecialites(prev => [...prev, form.category]);
          }
        }
      } else {
        console.log('Création d\'un nouveau plat');
        
        const { data, error } = await supabase
          .from('plat')
          .insert([{ 
            name: form.name, 
            price: parseFloat(form.price), 
            description: form.description, 
            category: form.category,
            image_url: imageUrl, 
            user_id: user.id 
          }])
          .select();

        if (error) {
          console.error('Erreur insertion:', error);
          if (error.message && (error.message.includes('permission') || error.message.includes('row-level security'))) {
            throw new Error('Erreur de permission. Vérifiez les politiques RLS dans Supabase.');
          }
          throw error;
        }
        
        console.log('Réponse insertion:', data);
        setSuccess('Plat ajouté avec succès');

        // Mettre à jour la liste des spécialités de l'utilisateur si c'est une nouvelle spécialité
        if (specialitesCategories.includes(form.category)) {
          setUserSpecialites(prev => [...prev, form.category]);
        }
      }

      resetForm();
      setTimeout(() => {
        fetchPlats();
        setCurrentPage(1); 
      }, 500);
    } catch (error) {
      console.error('Erreur complète:', error);
      setError('Erreur: ' + (error.message || String(error)));
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (plat) => {
    console.log('Édition du plat:', plat);
    setEditingPlat(plat);
    setForm({
      name: plat.name,
      price: plat.price,
      description: plat.description,
      category: plat.category || '',
      image: null,
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Confirmer la suppression du plat ?')) {
      try {
        console.log('Suppression du plat ID:', id);
        
        // Récupérer la catégorie du plat avant suppression
        const platToDelete = plats.find(plat => plat.id === id);
        
        const { error } = await supabase
          .from('plat')
          .delete()
          .eq('id', id);
        
        if (error) {
          console.error('Erreur suppression:', error);
          if (error.message && (error.message.includes('permission') || error.message.includes('row-level security'))) {
            throw new Error('Erreur de permission. Vérifiez les politiques RLS dans Supabase.');
          }
          throw error;
        }
        
        setSuccess('Plat supprimé avec succès');
        
        // Mettre à jour la liste des spécialités si nécessaire
        if (platToDelete && specialitesCategories.includes(platToDelete.category)) {
          setUserSpecialites(prev => prev.filter(cat => cat !== platToDelete.category));
        }
        
        fetchPlats();
      } catch (error) {
        console.error('Erreur lors de la suppression du plat:', error);
        setError('Erreur lors de la suppression du plat: ' + (error.message || String(error)));
      }
    }
  };

  return (
    <div className="content-wrapper">
      <div className="page-header">
        <h3 className="page-title">Gestion des plats</h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item" style={{color: "#0063cc"}}>Tables</li>
            <li className="breadcrumb-item active" aria-current="page">Plats</li>
          </ol>
        </nav>
      </div>

      <div className="row">
        <div className="col-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title">{editingPlat ? 'Modifier' : 'Ajouter'} un plat</h4>
              <form className="forms-sample" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Nom</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    name="name" 
                    placeholder="Nom" 
                    value={form.name} 
                    onChange={handleChange} 
                    required 
                  />
                </div>
                <div className="form-group">
                  <label>Prix (€)</label>
                  <input 
                    type="number" 
                    className="form-control" 
                    name="price" 
                    placeholder="Prix" 
                    value={form.price} 
                    onChange={handleChange} 
                    step="1"
                    min="0"
                    required 
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea 
                    className="form-control" 
                    name="description" 
                    placeholder="Description" 
                    rows="4" 
                    value={form.description} 
                    onChange={handleChange} 
                    required 
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="category">Catégorie</label>
                  <select
                    className="form-control"
                    id="category"
                    name="category"
                    value={form.category}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Sélectionner --</option>
                    
                    <optgroup label="Nos spécialités">
                      {specialitesCategories.map(category => (
                        <option 
                          key={category} 
                          value={category}
                          disabled={!editingPlat && !canAddSpecialite(category)}
                          style={{
                            color: !editingPlat && !canAddSpecialite(category) ? '#ccc' : 'inherit'
                          }}
                        >
                          {category}
                          {!editingPlat && !canAddSpecialite(category) && ' (Déjà ajouté)'}
                        </option>
                      ))}
                    </optgroup>
                    
                    <optgroup label="Nos plats">
                      {platsCategories.map(category => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                  
                  {!editingPlat && specialitesCategories.includes(form.category) && !canAddSpecialite(form.category) && (
                    <div className="alert alert-warning mt-2 p-2 small">
                      ⚠️ Vous ne pouvez ajouter qu'un seul plat dans cette catégorie spéciale.
                    </div>
                  )}
                  {editingPlat && specialitesCategories.includes(form.category) && (
                    <div className="alert alert-info mt-2 p-2 small">
                      ℹ️ Vous modifiez un plat de la catégorie spéciale "{form.category}".
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Image</label>
                  <div className="file-input-container">
                    <input
                      type="file"
                      className="file-input"
                      id="platImage"
                      name="image"
                      accept="image/*"
                      onChange={handleChange}
                    />
                    <label htmlFor="platImage" className="file-input-label">
                      <span className="file-input-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="17 8 12 3 7 8"></polyline>
                          <line x1="12" y1="3" x2="12" y2="15"></line>
                        </svg>
                      </span>
                      <span className="file-input-text">
                        {form.image ? form.image.name : "Choisir un fichier"}
                      </span>
                    </label>
                    {form.image && (
                      <button 
                        type="button" 
                        className="file-remove-btn"
                        onClick={() => setForm(prev => ({ ...prev, image: null }))}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    )}
                  </div>
                  {editingPlat && editingPlat.image_url && (
                    <div className="mt-2">
                      <span>Image actuelle: </span>
                      <img 
                        src={editingPlat.image_url} 
                        alt="Prévisualisation" 
                        style={{ maxWidth: '100px', maxHeight: '100px' }} 
                        className="mt-1"
                      />
                    </div>
                  )}
                </div>
                <button 
                  type="submit" 
                  className="btn btn-primary mr-2" 
                  disabled={uploading || (!editingPlat && specialitesCategories.includes(form.category) && !canAddSpecialite(form.category))}
                >
                  {uploading ? 'En cours...' : (editingPlat ? 'Modifier' : 'Ajouter')}
                </button>
                {editingPlat && (
                  <button 
                    type="button" 
                    className="btn btn-dark" 
                    onClick={resetForm}
                  >
                    Annuler
                  </button>
                )}
                {error && <div className="alert alert-danger mt-3">❌ {error}</div>}
                {success && <div className="alert alert-success mt-3">✅ {success}</div>}
              </form>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title">Liste des plats</h4>
              <div className="table-responsive custom-scroll">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Prix (€)</th>
                      <th>Description</th>
                      <th>Catégorie</th>
                      <th>Image</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPlats.map(plat => (
                      <tr key={plat.id}>
                        <td>{plat.name}</td>
                        <td>{plat.price} €</td>
                        <td>{plat.description}</td>
                        <td>
                          {plat.category}
                          {specialitesCategories.includes(plat.category) && (
                            <span className="text-muted ml-2" style={{fontSize: '0.85em'}}>
                              (spécialité)
                            </span>
                          )}
                        </td>
                        <td>
                          {plat.image_url && (
                            <img 
                              src={plat.image_url} 
                              alt={plat.name} 
                              style={{ maxWidth: '100px' }} 
                            />
                          )}
                        </td>
                        <td>
                          <button 
                            onClick={() => {handleEdit(plat); scrollToTop();}} 
                            className="btn btn-sm btn-primary mr-2"
                          >
                            Modifier
                          </button>
                          <button 
                            onClick={() => handleDelete(plat.id)} 
                            className="btn btn-sm btn-danger"
                          >
                            Supprimer
                          </button>
                        </td>
                      </tr>
                    ))}
                    {plats.length === 0 && (
                      <tr>
                        <td colSpan="6" className="text-center">
                          Aucun plat trouvé.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {plats.length > 0 && (
                <nav aria-label="Page navigation" className="mt-4">
                  <ul className="pagination justify-content-center">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={goToPreviousPage} aria-label="Previous">
                        <span aria-hidden="true">&laquo;</span>
                      </button>
                    </li>
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => paginate(page)}>
                          {page}
                        </button>
                      </li>
                    ))}
                    
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={goToNextPage} aria-label="Next">
                        <span aria-hidden="true">&raquo;</span>
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminRestaurant;